import { useState } from "react";
import BrandLogo from "../../components/BrandLogo";
import { Button } from "../../components/ui/button";
import { gluceraAssessmentCta } from "../../utils/gluceraTypography";
import { Label } from "../../components/ui/label";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import { useNavigate } from "react-router-dom";
import SessionStorageHelper from "../../utils/sessionStorageHelper";
import { Calendar, Info } from "lucide-react";

interface AgeData {
  age: number;
  dateOfBirth: string;
}

interface AgepageProps {
  onSubmit?: (data: AgeData) => void;
}

const Agepage = ({ onSubmit }: AgepageProps) => {
  const [dateOfBirth, setDateOfBirth] = useState<string>("2000-01-01");
  const navigate = useNavigate();

  // Calculate age from date of birth
  const calculateAge = (dob: string): number => {
    if (!dob) {
      return 0;
    }
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  // Format date to dd-mm-yyyy
  const formatDateToDDMMYYYY = (dateStr: string): string => {
    if (!dateStr) {
      return "";
    }
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        return "";
      }
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return "";
    }
  };

  const calculatedAge = calculateAge(dateOfBirth);

  // Validate date is not in future and is valid
  const isValidDate = dateOfBirth
    ? !isNaN(new Date(dateOfBirth).getTime()) &&
      new Date(dateOfBirth) <= new Date()
    : false;

  const isValidAge =
    dateOfBirth !== "" &&
    isValidDate &&
    calculatedAge >= 13 &&
    calculatedAge <= 150;

  const handleNext = () => {
    if (isValidAge && isValidDate) {
      const formattedDate = formatDateToDDMMYYYY(dateOfBirth);

      // Save date_of_birth for validation (age is calculated from this)
      SessionStorageHelper.saveField("date_of_birth", formattedDate);

      // Store age separately for UI use (not in API data)
      sessionStorage.setItem("calculated_age", calculatedAge.toString());

      if (onSubmit) {
        onSubmit({ age: calculatedAge, dateOfBirth: formattedDate });
      }

      navigate("/glucera" + GluceraNavigationRoutes.HEIGHT_WEIGHT_PAGE);
    }
  };

  return (
    <div className="h-screen w-screen overflow-hidden bg-background">
      <div className="mx-auto flex h-full w-full max-w-md flex-col">
        {/* Header with Progress */}
        <div className="space-y-4 px-4 pb-4 pt-safe">
          {/* <button
            onClick={handleBack}
            className="w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button> */}
          <BrandLogo />

          {/* Assessment Progress */}
          <p className="text-center text-sm text-muted-foreground">
            Assessment 3 of 11
          </p>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col px-4 py-2 sm:px-6 sm:py-4">
          <div className="mb-4 space-y-1 sm:mb-6 sm:space-y-2">
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">
              What Is Your Date Of Birth?
            </h1>
            <p className="text-xs text-muted-foreground sm:text-sm">
              Your date of birth helps us personalize your diabetes management
              content
            </p>
          </div>

          <div className="mb-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="dateOfBirth" className="text-base font-semibold">
                Enter your date of birth
              </Label>
              <div className="relative">
                <div className="relative flex items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3.5 shadow-sm">
                  <Calendar className="h-5 w-5 text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">
                      {dateOfBirth
                        ? new Date(dateOfBirth).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        : "Select your date of birth"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Tap to change your birth date
                    </p>
                  </div>
                  {/* Native date picker overlay for mobile-friendly scroll wheel selection */}
                  <input
                    type="date"
                    id="dateOfBirth"
                    value={dateOfBirth}
                    max={new Date().toISOString().split("T")[0]}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    onClick={(e) => {
                      try {
                        e.currentTarget.showPicker();
                      } catch (err) {
                        console.warn("showPicker not supported", err);
                      }
                    }}
                    className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                  />
                </div>
              </div>
            </div>

            {!dateOfBirth && (
              <div className="flex items-start gap-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2.5 dark:border-blue-900/40 dark:bg-blue-950/20">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
                <p className="text-xs text-blue-800 dark:text-blue-300 sm:text-sm">
                  Tap the field to pick your date of birth. You must be at least
                  13 years old — we use this only to personalize your insights.
                </p>
              </div>
            )}

            {dateOfBirth && !isValidDate && (
              <p className="text-sm text-destructive">
                Please select a date that is not in the future
              </p>
            )}

            {dateOfBirth && isValidDate && calculatedAge < 13 && (
              <p className="text-sm text-destructive">
                You must be at least 13 years old
              </p>
            )}

            {dateOfBirth && isValidDate && calculatedAge > 150 && (
              <p className="text-sm text-destructive">
                Age cannot be more than 150 years
              </p>
            )}

            {dateOfBirth && isValidAge && isValidDate && (
              <p className="text-sm font-medium text-green-600 dark:text-green-400">
                ✓ Age calculated: {calculatedAge} years
              </p>
            )}
          </div>

          {/* Button Container */}
          <div className="mt-auto">
            <Button
              onClick={handleNext}
              disabled={!isValidAge || !isValidDate}
              className={gluceraAssessmentCta}
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {/* Safe area padding */}
      <div className="pb-safe p-2" />
    </div>
  );
};

export default Agepage;
